import { EventType } from '../interfaces';
import { diagramColors } from '../theme/colors';
import { useFleetEventTypesQuery } from '../queries';
import { useHasAccess } from './useHasAccess';
import { EventTypeIdentifier, EventTypePalette } from '../enums';

const overrideColor = (eventType: EventType) => {
  if (eventType && eventType?.marker_color_hex === 'F00') {
    return '#ACDDDE';
  }
  if (eventType && eventType?.marker_color_hex === 'D00') {
    return '#FFBF00';
  }
  if (eventType && eventType?.marker_color_hex === 'EFFFB5') {
    return '#ccb653';
  }
  if (eventType && eventType?.marker_color_hex === 'AFFFB5') {
    return '#87ee8e';
  }
  if (eventType && eventType?.marker_color_hex === 'FFE0B5') {
    return '#f9cb8b';
  }
  if (eventType && eventType?.marker_color_hex === 'EGGFB5') {
    return '#d4a3a3';
  }
  if (eventType && eventType?.identifier === EventTypeIdentifier.ROADWORKS) {
    return EventTypePalette.ROADWORKS;
  }
  return eventType ? `#${eventType.marker_color_hex}` : '#16a1b8';
};

export const eventTypesWithChangedColors = (eventTypes: EventType[]) => {
  // firstly we need to map all event types with override color
  const mappedWithOverrideColor = eventTypes.map((eventType) => {
    return {
      ...eventType,
      marker_color_hex: overrideColor(eventType),
    };
  });
  // we have situation when we have duplicated color multiple times
  // so we need to change color for each duplicated color
  // and return all event types with unique changed colors
  // so we must doing this recursively until we have all unique colors
  // but we must remember that we can't change color to the same color
  // and we must avoid infinite loop

  const colors = Object.values(diagramColors);
  const changedColors = new Set<string>();
  const changedEventTypes = new Set<EventType>();
  const changeColor = (eventType: EventType) => {
    const color = eventType.marker_color_hex;
    const newColor = colors.find((c) => !changedColors.has(c) && c !== color);
    if (newColor) {
      changedColors.add(newColor);
      changedEventTypes.add({ ...eventType, marker_color_hex: newColor });
    } else {
      changeColor(eventType);
    }
  };

  mappedWithOverrideColor.forEach((eventType) => {
    if (!changedColors.has(eventType.marker_color_hex)) {
      changedColors.add(eventType.marker_color_hex);
      changedEventTypes.add(eventType);
    } else {
      changeColor(eventType);
    }
  });

  return Array.from(changedEventTypes);
};

export const useEventTypes = () => {
  const { data, isLoading } = useFleetEventTypesQuery();

  const eventTypes = eventTypesWithChangedColors(data || []);

  const hasAccess = useHasAccess();
  if (isLoading) {
    return [];
  }

  return hasAccess ? eventTypes : eventTypes?.filter((item) => !item.hidden) || [];
};
